// Map lenses: one value per hex (scalar 0..1 or category index; NaN = no data) plus legend + sources.
//
//   country lenses   a per-state table painted through the DEFAULT-worldview owner at D, so a
//                    leader switch never changes a number (ARCHITECTURE §5). Scales are fixed over
//                    every year of the series, so colours stay comparable while scrubbing. A state's
//                    newest value older than STALE_YEARS is left unpainted (`year` is the newest
//                    painted year, `yearFrom` the oldest).
//   leader lenses    political (relation), alliance, UNGA alignment: relative to the leader's state
//                    and painted through the leader's owner array (the borders on screen).
//   hex lenses       population density, resources, freshness, conflict (control per hex).

import { HEX_COUNT } from '../core/grid.ts';
import type { DateStr, SourceId } from '../core/schema.ts';
import { RESOURCE } from '../core/schema.ts';
import { yearOf } from '../core/dates.ts';
import { CONTROL, type LensResult, type Relation, type WorldSnapshot } from './api.ts';
import type { ActorTable } from './actors.ts';
import { BASIS } from './control.ts';
import { latest, rangeOf, series, sourcesFor, sourcesOf, worldTotal, type IndicatorIndex } from './indicators.ts';
import type { PovKey } from './owners.ts';
import { blocsOf, membersAt, treatyAllies, type Diplo } from './relations.ts';
import { fmtNum, labelledRamp, rampLegend, toUnit, type Scale, type ScaleKind } from './scale.ts';
import { FRESH_DAYS, STALE_DAYS } from './staleness.ts';
import { uniq } from './util.ts';

/** sites.grid.bin `pop` comes from GHS-POP epoch 2025 (tools/bake/worldciv_bake/sites.py SRC_POP). */
const POP_SOURCE = 'site:ghsl-pop-e2025';
const POP_YEAR = 2025;

/** A country value older than this many years is not the state's picture now: it is left unpainted. */
const STALE_YEARS = 3;

/** Densities below this draw as the lowest colour (display floor, people per km²). */
const DENSITY_FLOOR = 1;

const MAX_BYTE = 255;
const INTERSTATE = 'interstate';

// Display colours for category lenses (UI defaults, not data).
const COLOR = {
  self: '#f2c14e',
  war: '#c0392b',
  ally: '#2e86de',
  friend: '#27ae60',
  neutral: '#95a5a6',
  denounced: '#8e44ad',
  treaty: '#5dade2',
  none: '#5d6d7e',
  // Conflict lens: told apart at a glance under normal, protan, deutan and tritan vision (test/world/cvd.ts).
  // Control codes are saturated; the theatre they sit in is a lighter tint of the war red or a neutral.
  occupied: '#790606',
  contested: '#f9bc06',
  annexed: '#9019e6',
  breakaway: '#25c0f4',
  interstate: '#ec6a51',
  otherWar: '#dddbd5',
  calm: '#34495e',
  unknown: '#6c9393',
} as const;

export interface LensEnv {
  ix: IndicatorIndex;
  actors: ActorTable;
  diplo: Diplo;
  pop: Float32Array;
  area: Float32Array;
  land: Uint8Array;
  resource: Uint8Array;
  depositSources: SourceId[];
  /** Lens scales computed once per world (they span every year of a series). */
  scales: Map<string, Scale>;
}

export interface LensCtx {
  date: DateStr;
  /** iso3 of the viewing state (a leader's state, or a state view's). */
  self: string;
  /** The leader's point of view; null for a state view (default worldview). */
  pov: PovKey | null;
  snap: WorldSnapshot & { controlBasis: Uint8Array };
  /** Default-worldview owner at the date. */
  defOwner: Uint16Array;
  /** Control episode sources in force at the date. */
  controlSources: SourceId[];
}

/** A lens result that also says how far back its painted values reach (`year` is the newest). */
export type AgedLens = LensResult & { yearFrom?: number };

type Build = (env: LensEnv, ctx: LensCtx) => AgedLens;

// ─── painting ────────────────────────────────────────────────────────────────────────────

/** Per-hex values from a per-actor table (NaN where the owner has none). */
function paint(owner: Uint16Array, table: Float32Array): Float32Array {
  const out = new Float32Array(HEX_COUNT);
  for (let h = 0; h < HEX_COUNT; h++) {
    const o = owner[h];
    out[h] = o === 0 || o >= table.length ? NaN : table[o];
  }
  return out;
}

function emptyTable(env: LensEnv): Float32Array {
  return new Float32Array(env.actors.maxCountry + 1).fill(NaN);
}

/** Every country index with its iso3. */
function eachCountry(env: LensEnv, fn: (index: number, iso3: string) => void): void {
  for (let i = 1; i <= env.actors.maxCountry; i++) {
    const a = env.actors.list[i];
    if (a) {
      fn(i, a.id);
    }
  }
}

// ─── country indicator lenses ────────────────────────────────────────────────────────────

interface IndicatorLens {
  indicator: string;
  scale: ScaleKind;
  /** 'unit': the index already runs 0..1; 'data': min..max over all years. */
  bounds: 'unit' | 'data';
  /** 'share': value over the world total of its year. */
  measure: 'value' | 'share';
  fmt: (v: number) => string;
}

function measured(env: LensEnv, spec: IndicatorLens, iso3: string, year: number): [number, number] | null {
  const hit = latest(env.ix, spec.indicator, iso3, year);
  if (!hit || spec.measure === 'value') {
    return hit;
  }
  const total = worldTotal(env.ix, spec.indicator, hit[0]);
  return total > 0 ? [hit[0], hit[1] / total] : null;
}

/** Range of the measure over every state and year. */
function shareRange(env: LensEnv, spec: IndicatorLens): [number, number] | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const iso3 of env.ix.states) {
    for (const [y] of series(env.ix, spec.indicator, iso3) ?? []) {
      const m = measured(env, spec, iso3, y);
      if (m && m[1] > 0) {
        lo = Math.min(lo, m[1]);
        hi = Math.max(hi, m[1]);
      }
    }
  }
  return lo <= hi ? [lo, hi] : null;
}

function scaleOf(env: LensEnv, spec: IndicatorLens): Scale {
  if (spec.bounds === 'unit') {
    return { kind: spec.scale, lo: 0, hi: 1 };
  }
  const positive = spec.scale === 'log' ? 'positive' : 'any';
  const r = spec.measure === 'share' ? shareRange(env, spec) : rangeOf(env.ix, spec.indicator, positive);
  return { kind: spec.scale, lo: r ? r[0] : 0, hi: r ? r[1] : 1 };
}

/** A scale cached on the env, so each world computes it once. */
function cachedScale(env: LensEnv, id: string, make: () => Scale): Scale {
  const hit = env.scales.get(id);
  if (hit) {
    return hit;
  }
  const s = make();
  env.scales.set(id, s);
  return s;
}

function indicatorLens(spec: IndicatorLens): (id: string) => Build {
  return (id) => (env, ctx) => {
    const s = cachedScale(env, id, () => scaleOf(env, spec));
    const year = yearOf(ctx.date);
    const table = emptyTable(env);
    let newest = 0;
    let oldest = Infinity;
    const sources = new Set<SourceId>();
    eachCountry(env, (i, iso3) => {
      const m = measured(env, spec, iso3, year);
      if (!m || year - m[0] > STALE_YEARS) {
        return;
      }

      table[i] = toUnit(s, m[1]);
      newest = Math.max(newest, m[0]);
      oldest = Math.min(oldest, m[0]);
      sourcesFor(env.ix, spec.indicator, iso3, m[0]).forEach((id) => sources.add(id));
    });
    return {
      id, kind: 'scalar', values: paint(ctx.defOwner, table), legend: rampLegend(s, spec.fmt),
      sources: [...sources], year: newest || undefined, yearFrom: newest ? oldest : undefined,
    };
  };
}

const usd = (v: number) => `$${fmtNum(v)}`;
const share = (v: number) => `${(v * 100).toPrecision(2)}%`;

// ─── leader lenses ───────────────────────────────────────────────────────────────────────

const RELATION_ORDER: Relation[] = ['self', 'war', 'ally', 'denounced', 'friend', 'neutral'];
const RELATION_LABEL: Record<Relation, string> = {
  self: 'Your state', war: 'At war', ally: 'Ally', denounced: 'Denounced', friend: 'Friend', neutral: 'Neutral',
};

const political: Build = (env, ctx) => {
  const table = new Float32Array(env.actors.list.length).fill(NaN);
  const sources: SourceId[] = [];
  for (const [i, info] of ctx.snap.relations) {
    table[i] = RELATION_ORDER.indexOf(info.relation);
    sources.push(...info.sources);
  }
  return {
    id: 'political', kind: 'category', values: paint(ctx.snap.owner, table),
    legend: RELATION_ORDER.map((r) => ({ label: RELATION_LABEL[r], color: COLOR[r] })), sources: uniq(sources),
  };
};

interface Category {
  label: string;
  color: string;
  sources: SourceId[];
}

/** Alliance categories for a leader: own state, own blocs, treaty allies, other blocs, none. */
function allianceCats(env: LensEnv, self: string, date: DateStr): { cats: Category[]; of: Map<string, number> } {
  const d = env.diplo;
  const cats: Category[] = [{ label: 'Your state', color: COLOR.self, sources: [] }];
  const of = new Map<string, number>([[self, 0]]);
  const assign = (ids: string[], cat: Category) => {
    const fresh = ids.filter((id) => !of.has(id));
    if (fresh.length === 0) {
      return;
    }
    cats.push(cat);
    fresh.forEach((id) => of.set(id, cats.length - 1));
  };

  const defence = [...d.blocs.values()].filter((b) => b.mutual_defense);
  const mine = new Set(blocsOf(d, self, date).map((m) => m.bloc));
  const ordered = [...defence.filter((b) => mine.has(b.id)), ...defence.filter((b) => !mine.has(b.id))];
  const treaty = [...treatyAllies(d, self, date)];

  // Own blocs first, then bilateral treaty allies, then everyone else's blocs.
  ordered.slice(0, mine.size).forEach((b) => assign(membersAt(d, b.id, date).map((m) => m.member), { label: b.name, color: b.color, sources: b.sources }));
  assign(treaty.map(([id]) => id), { label: 'Treaty ally', color: COLOR.treaty, sources: uniq(treaty.flatMap(([, w]) => w.sources)) });
  ordered.slice(mine.size).forEach((b) => assign(membersAt(d, b.id, date).map((m) => m.member), { label: b.name, color: b.color, sources: b.sources }));
  cats.push({ label: 'No mutual-defence pact', color: COLOR.none, sources: [] });
  return { cats, of };
}

const alliance: Build = (env, ctx) => {
  const { cats, of } = allianceCats(env, ctx.self, ctx.date);
  const none = cats.length - 1;
  const table = emptyTable(env);
  eachCountry(env, (i, iso3) => {
    table[i] = of.get(iso3) ?? none;
  });
  return {
    id: 'alliance', kind: 'category', values: paint(ctx.snap.owner, table),
    legend: cats.map((c) => ({ label: c.label, color: c.color })), sources: uniq(cats.flatMap((c) => c.sources)),
  };
};

const UNGA = 'unga_ideal';

const unga: Build = (env, ctx) => {
  const year = yearOf(ctx.date);
  const self = ctx.self;
  const range = rangeOf(env.ix, UNGA, 'any');
  const span = range ? range[1] - range[0] : 0;
  const me = latest(env.ix, UNGA, self, year);
  const table = emptyTable(env);
  let newest = 0;
  eachCountry(env, (i, iso3) => {
    const p = latest(env.ix, UNGA, iso3, year);
    if (!me || !p || !(span > 0) || year - p[0] > STALE_YEARS || year - me[0] > STALE_YEARS) {
      return;
    }
    table[i] = 1 - Math.abs(p[1] - me[1]) / span;
    newest = Math.max(newest, p[0], me[0]);
  });
  const s: Scale = { kind: 'linear', lo: 0, hi: span };
  const legend = rampLegend(s, (v) => `${(span - v).toFixed(2)} apart from ${self}`);
  return { id: 'unga', kind: 'scalar', values: paint(ctx.snap.owner, table), legend, sources: sourcesOf(env.ix, UNGA), year: newest || undefined };
};

// ─── hex lenses ──────────────────────────────────────────────────────────────────────────

function densityRange(env: LensEnv): Scale {
  let hi = DENSITY_FLOOR;
  for (let h = 0; h < HEX_COUNT; h++) {
    if (env.land[h] > 0 && env.area[h] > 0) {
      hi = Math.max(hi, env.pop[h] / env.area[h]);
    }
  }
  return { kind: 'log', lo: DENSITY_FLOOR, hi };
}

const population: Build = (env, ctx) => {
  const s = cachedScale(env, 'population', () => densityRange(env));
  const values = new Float32Array(HEX_COUNT);
  for (let h = 0; h < HEX_COUNT; h++) {
    values[h] = env.land[h] > 0 && env.area[h] > 0 ? toUnit(s, env.pop[h] / env.area[h]) : NaN;
  }
  // Only the 2025 grid is baked: before that year the legend says the picture is from later.
  const later = yearOf(ctx.date) < POP_YEAR ? ` (${POP_YEAR} grid, after this date)` : '';
  const legend = rampLegend(s, (v) => `${fmtNum(v)} /km²${later}`);
  return { id: 'population', kind: 'scalar', values, legend, sources: [POP_SOURCE], year: POP_YEAR };
};

const RESOURCE_NAMES = Object.entries(RESOURCE).filter(([, v]) => v !== RESOURCE.NONE).sort((a, b) => a[1] - b[1]);
const RESOURCE_COLORS = ['#1b1b1b', '#7fb3d5', '#5d4037', '#c0ca33', '#26c6da', '#3949ab', '#ab47bc', '#e67e22', '#90a4ae', '#f1c40f', '#66bb6a', '#212f3d'];

function titleCase(key: string): string {
  return key.toLowerCase().split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
}

const resources: Build = (env) => {
  const values = new Float32Array(HEX_COUNT);
  for (let h = 0; h < HEX_COUNT; h++) {
    const r = env.resource[h];
    values[h] = r === RESOURCE.NONE ? NaN : r - 1;
  }
  const legend = RESOURCE_NAMES.map(([k], i) => ({ label: titleCase(k), color: RESOURCE_COLORS[i] ?? COLOR.none }));
  return { id: 'resources', kind: 'category', values, legend, sources: env.depositSources };
};

const FRESH_LABELS = [
  `≤ ${FRESH_DAYS} days`, '', `~${Math.round((FRESH_DAYS + STALE_DAYS) / 2)} days`, '', `≥ ${STALE_DAYS} days or none`,
] as const;

/** Derived from every dated fact, so it cites none itself. */
const freshness: Build = (_env, ctx) => {
  const values = new Float32Array(HEX_COUNT);
  const st = ctx.snap.staleness;
  for (let h = 0; h < HEX_COUNT; h++) {
    values[h] = st[h] / MAX_BYTE;
  }
  return { id: 'freshness', kind: 'scalar', values, legend: labelledRamp(FRESH_LABELS), sources: [] };
};

const CONFLICT_CATS = [
  { label: 'Occupied', color: COLOR.occupied },
  { label: 'Contested', color: COLOR.contested },
  { label: 'Annexation claimed', color: COLOR.annexed },
  { label: 'Breakaway', color: COLOR.breakaway },
  { label: 'Interstate war theatre', color: COLOR.interstate },
  { label: 'Other war theatre', color: COLOR.otherWar },
  { label: 'No active war', color: COLOR.calm },
  { label: 'Control unknown', color: COLOR.unknown },
] as const;
const THEATRE_INTERSTATE = 4;
const THEATRE_OTHER = 5;
const CALM = 6;
const UNKNOWN = 7;

/** Category of each control code (the first four legend entries). */
const CONTROL_CATEGORY: Record<number, number> = {
  [CONTROL.OCCUPIED]: 0,
  [CONTROL.CONTESTED]: 1,
  [CONTROL.ANNEXATION_CLAIMED]: 2,
  [CONTROL.BREAKAWAY]: 3,
};

const conflict: Build = (env, ctx) => {
  const table = emptyTable(env);
  const interstate = new Set(ctx.snap.activeWars.filter((w) => w.kind === INTERSTATE).flatMap((w) => w.theatre));
  const other = new Set(ctx.snap.activeWars.flatMap((w) => w.theatre));
  eachCountry(env, (i, iso3) => {
    table[i] = interstate.has(iso3) ? THEATRE_INTERSTATE : other.has(iso3) ? THEATRE_OTHER : CALM;
  });

  // Control codes win over the theatre colour. A hex inside a declared gap of a control series carries
  // the code CONTESTED, but nobody observed it: it is "Control unknown", not a contest.
  const values = paint(ctx.defOwner, table);
  const { control, controlBasis } = ctx.snap;
  for (let h = 0; h < HEX_COUNT; h++) {
    if (control[h] === CONTROL.NONE) {
      continue;
    }
    values[h] = controlBasis[h] === BASIS.UNKNOWN ? UNKNOWN : CONTROL_CATEGORY[control[h]];
  }
  const sources = uniq([...ctx.snap.activeWars.flatMap((w) => w.sources), ...ctx.controlSources]);
  return { id: 'conflict', kind: 'category', values, legend: CONFLICT_CATS.map((c) => ({ ...c })), sources };
};

// ─── registry ────────────────────────────────────────────────────────────────────────────

const LENSES: Array<{ id: string; name: string; build: Build }> = [
  { id: 'political', name: 'Political', build: political },
  { id: 'alliance', name: 'Alliances', build: alliance },
  { id: 'conflict', name: 'Conflict', build: conflict },
  { id: 'economy', name: 'GDP per capita', build: indicatorLens({ indicator: 'gdp_pc_usd', scale: 'log', bounds: 'data', measure: 'value', fmt: usd })('economy') },
  { id: 'population', name: 'Population density', build: population },
  { id: 'democracy', name: 'Electoral democracy', build: indicatorLens({ indicator: 'democracy', scale: 'linear', bounds: 'unit', measure: 'value', fmt: (v) => v.toFixed(2) })('democracy') },
  { id: 'co2', name: 'CO₂ per capita', build: indicatorLens({ indicator: 'co2_pc_t', scale: 'log', bounds: 'data', measure: 'value', fmt: (v) => `${fmtNum(v)} t` })('co2') },
  { id: 'unga', name: 'UNGA alignment', build: unga },
  { id: 'milex', name: 'Military spending share', build: indicatorLens({ indicator: 'milex_usd', scale: 'log', bounds: 'data', measure: 'share', fmt: share })('milex') },
  { id: 'hdi', name: 'Human development', build: indicatorLens({ indicator: 'hdi', scale: 'linear', bounds: 'unit', measure: 'value', fmt: (v) => v.toFixed(2) })('hdi') },
  { id: 'resources', name: 'Resources', build: resources },
  { id: 'freshness', name: 'Source age', build: freshness },
];

export function lensList(): Array<{ id: string; name: string }> {
  return LENSES.map(({ id, name }) => ({ id, name }));
}

export function buildLens(id: string, env: LensEnv, ctx: LensCtx): AgedLens {
  const lens = LENSES.find((l) => l.id === id);
  if (!lens) {
    throw new Error(`world: unknown lens ${id}`);
  }
  return lens.build(env, ctx);
}
